import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlignLeft, Ban, CalendarClock, CalendarX2, Check, CircleHelp, MapPin, Repeat, User, X } from 'lucide-react';
import { apiPublica, errorDe } from './apiPublica';
import MarcoPublico, { Aviso, Cargando } from './MarcoPublico';
import { esUrl, trozosConEnlaces } from '../../lib/calendario';

const ESTADOS = ['aceptado', 'quizas', 'rechazado'];
const RESPUESTA = { aceptado: 'Asistirás', quizas: 'Quizás asistas', rechazado: 'No asistirás' };

/**
 * Responder una invitación sin cuenta (`/rsvp/:token`, botones del correo de invitación). Con `?r=` (el botón
 * Sí/Quizás/No del propio correo) se PRESELECCIONA esa respuesta y se pide un clic para confirmarla (K11,
 * auditoría 2026-10-05): antes se respondía sola al cargar, y los escáneres de enlaces de los correos
 * (Defender, Safe Links), que abren y ejecutan las páginas, «pulsaban» Sí, Quizás y No por la persona.
 */
export default function Rsvp() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [fallo, setFallo] = useState('');           // los errores ya no se pintan como éxito (K11)
  const [propuesta, setPropuesta] = useState(null); // la respuesta del botón del correo, a confirmar
  const yaRespondio = useRef(false);

  const responder = async (estado) => {
    setEnviando(true);
    setMensaje('');
    setFallo('');
    try {
      await apiPublica.post(`/calendar/rsvp/${encodeURIComponent(token)}`, { estado });
      setInv((i) => (i ? { ...i, invitado: { ...i.invitado, estado } } : i));
      setPropuesta(null);
      setMensaje(`Respuesta enviada: ${RESPUESTA[estado].toLowerCase()}. El organizador ya lo sabe.`);
    } catch (e) {
      setFallo(errorDe(e, 'No se pudo enviar tu respuesta'));
    } finally {
      setEnviando(false);
    }
  };

  useEffect(() => {
    let vivo = true;
    apiPublica.get(`/calendar/rsvp/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (!vivo) return;
        setInv(data);
        const r = params.get('r');
        if (r && ESTADOS.includes(r) && !yaRespondio.current && !data.cancelado) {
          yaRespondio.current = true;
          const limpio = new URLSearchParams(params);
          limpio.delete('r');
          setParams(limpio, { replace: true });
          if ((data.invitado || {}).estado !== r) setPropuesta(r);
        }
      })
      .catch((e) => vivo && setError(errorDe(e, 'Esta invitación ya no existe')));
    return () => { vivo = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <MarcoPublico titulo="Invitación"><Aviso icono={<CalendarX2 className="pub-aviso-ico" />} titulo="Invitación no disponible" texto={error} /></MarcoPublico>;
  if (!inv) return <MarcoPublico titulo="Invitación"><Cargando /></MarcoPublico>;

  const estado = inv.invitado?.estado;
  return (
    <MarcoPublico titulo={inv.titulo || 'Invitación'}>
      <div className="pub-cita" data-testid="pub-rsvp">
        <p className="pub-etiqueta" style={{ color: inv.cancelado ? 'var(--danger)' : undefined }}>{inv.cancelado ? 'Evento cancelado' : 'Invitación'}</p>
        <h1 className="pub-titulo" style={{ textDecoration: inv.cancelado ? 'line-through' : undefined }}>{inv.titulo || '(Sin título)'}</h1>
        {inv.cuando && <p className="pub-dato pub-dato--fuerte"><CalendarClock />{inv.cuando}</p>}
        {inv.repeticion && <p className="pub-dato"><Repeat />{inv.repeticion}</p>}
        {inv.ubicacion && <p className="pub-dato"><MapPin />{esUrl(inv.ubicacion) ? <a href={inv.ubicacion} target="_blank" rel="noopener noreferrer nofollow">{inv.ubicacion}</a> : inv.ubicacion}</p>}
        {inv.organizador?.correo && <p className="pub-dato"><User />Organiza {inv.organizador.nombre || inv.organizador.correo}</p>}
        {inv.descripcion && (
          <p className="pub-dato pub-dato--arriba"><AlignLeft />
            <span className="pub-descripcion">
              {trozosConEnlaces(inv.descripcion).map((t, i) => (t.enlace
                ? <a key={i} href={t.enlace} target="_blank" rel="noopener noreferrer nofollow">{t.enlace}</a>
                : <React.Fragment key={i}>{t.texto}</React.Fragment>))}
            </span>
          </p>
        )}

        {inv.cancelado ? (
          <p className="pub-error"><Ban className="h-4 w-4" /> El organizador canceló este evento.</p>
        ) : (
          <div className="pub-rsvp">
            <p className="pub-ayuda">Invitación para {inv.invitado?.nombre ? `${inv.invitado.nombre} (${inv.invitado.correo})` : inv.invitado?.correo}</p>
            <p className="pub-pregunta">{estado && estado !== 'pendiente' ? `Tu respuesta: ${RESPUESTA[estado]}` : '¿Asistirás?'}</p>
            <div className="pub-acciones">
              {[['aceptado', 'Sí', Check], ['quizas', 'Quizás', CircleHelp], ['rechazado', 'No', X]].map(([e, t, Ico]) => (
                <button
                  key={e}
                  type="button"
                  className={`pub-boton pub-boton--borde ${(propuesta || estado) === e ? 'is-activa' : ''}`}
                  disabled={enviando}
                  onClick={() => responder(e)}
                  aria-pressed={estado === e}
                  data-testid={`pub-rsvp-${e}`}
                >
                  <Ico className="h-4 w-4" />{t}
                </button>
              ))}
            </div>
            {propuesta && !mensaje && (
              <div className="pub-acciones" data-testid="pub-rsvp-confirmar">
                <button type="button" className="pub-boton" disabled={enviando} onClick={() => responder(propuesta)}>
                  Confirmar: {RESPUESTA[propuesta]}
                </button>
              </div>
            )}
            {mensaje && <p className="pub-exito" role="status">{mensaje}</p>}
            {fallo && <p className="pub-error" role="alert">{fallo}</p>}
          </div>
        )}
      </div>
    </MarcoPublico>
  );
}
